import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { ArrowLeft, CheckCircle2, Clock, Loader2, Lock, Ticket, XCircle, MinusCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, romeDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hotColor } from "../components/ProbabilityWidget";
import { NotifyToggle } from "../components/NotifyToggle";
import { WinShowcase } from "../components/WinShowcase";
import { ShareSlipButton } from "../components/ShareSlip";
import { WinsGallery } from "../components/WinsGallery";
import SlipsHistory from "../components/SlipsHistory";

const RESULT = {
  won: { icon: CheckCircle2, color: "#39FF14", label: "Vinta" },
  lost: { icon: XCircle, color: "#FF3B30", label: "Persa" },
  void: { icon: MinusCircle, color: "#A1A1AA", label: "Annullata" },
  pending: { icon: Clock, color: "#71717A", label: "In attesa" },
};

const kickoff = (iso) => new Date(iso).toLocaleString("it-IT", { weekday: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });

function LiveLine({ x }) {
  const { live } = x;
  const extra = x.market.startsWith("C") && live.corners != null ? ` · corner ${live.corners}` : x.market.startsWith("K") && live.cards != null ? ` · cartellini ${live.cards}` : "";
  return (
    <div className="flex items-center gap-2 mt-1 text-[11px] font-black" data-testid={`slip-live-${x.match_id}`}>
      <span className="flex items-center gap-1 text-[#FF3B30]"><span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" /> LIVE {live.clock}</span>
      <span className="mono">{live.score}{extra}</span>
      {x.market === "SCO" && (live.scored != null
        ? <span className="text-[#39FF14]" data-testid={`scorer-scored-${x.match_id}`}>Ha segnato {live.scored}</span>
        : <span className="text-[#A1A1AA]">In campo, non ancora a segno</span>)}
      {x.market === "YC" && (live.booked != null
        ? <span className="text-[#39FF14]" data-testid={`booked-live-${x.player_id}`}>Ammonito {live.booked}</span>
        : <span className="text-[#A1A1AA]">In campo, non ancora ammonito</span>)}
    </div>
  );
}

function Selection({ x }) {
  const R = RESULT[x.result] || RESULT.pending;
  return (
    <div className="border border-[#27272A] p-3" data-testid={`slip-selection-${x.match_id}`}>
      <div className="flex items-start gap-3">
        <R.icon size={16} style={{ color: R.color }} className="shrink-0 mt-0.5" title={R.label} />
        <div className="min-w-0 flex-1">
          <Link to={`/match/${encodeURIComponent(x.match_id)}`} className="font-bold leading-tight hover:text-[#39FF14] transition-colors flex items-center gap-1.5 flex-wrap">
            {x.home_logo && <img src={x.home_logo} alt="" className="w-4 h-4 object-contain" />}{x.home}
            <span className="text-[#71717A]">-</span>
            {x.away_logo && <img src={x.away_logo} alt="" className="w-4 h-4 object-contain" />}{x.away}
          </Link>
          {x.live && <LiveLine x={x} />}
          <div className="text-[10px] text-[#71717A] mono mt-0.5">
            {x.league} · {kickoff(x.kickoff)}{x.final_score ? ` · finale ${x.final_score}` : ""}
            {x.final_score && x.market.startsWith("C") && x.final_corners != null ? ` · corner ${x.final_corners}` : ""}
            {x.final_score && x.market.startsWith("K") && x.final_cards != null ? ` · cartellini ${x.final_cards}` : ""}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="mono font-black text-lg leading-none" style={{ color: hotColor(x.probability) }} data-testid={`slip-sel-prob-${x.match_id}`}>{x.probability}%</div>
          <div className="text-[9px] label mt-1">probabilità</div>
        </div>
      </div>
      <div className="mt-2 flex items-start gap-2">
        <span
          className={`shrink-0 border px-2 py-0.5 text-[11px] font-black uppercase tracking-wider ${
            x.market.startsWith("C") ? "border-[#007AFF]/60 text-[#007AFF]" : x.market.startsWith("K") ? "border-[#FFCC00]/60 text-[#FFCC00]" : "bg-[#1A1A1A] border-[#27272A]"
          }`}
          data-testid={`slip-market-${x.market}`}
        >
          {x.market_label}
        </span>
        <p className="text-xs text-[#A1A1AA] leading-snug">{x.reasoning}</p>
      </div>
      {x.evidence && (
        <div className="mt-2 text-[10px] mono text-[#71717A] leading-snug" data-testid={`slip-evidence-${x.match_id}`}>
          {x.evidence}
          {x.model_probability != null && ` · modello ${x.model_probability}%`}
          {x.ai_probability != null && ` · AI ${x.ai_probability}%`}
        </div>
      )}
    </div>
  );
}

const ACCENTS = ["#39FF14", "#007AFF", "#FFCC00", "#FF2D95", "#FF3B30", "#00E5FF", "#A3E635", "#F97316", "#E879F9", "#22D3EE"];
const slipLabel = (s) => s.label || (s.kind === "corner" ? `Mista corner · quota ${s.target}` : `Schedina quota ${s.target}`);
const slipKey = (s) => s.id || String(s.target);

function ScorerPending() {
  return (
    <div className="surface-raised p-6 border-t-2 border-[#FFCC00] flex flex-col justify-center" data-testid="scorer-pending">
      <div className="label text-[10px]">3 marcatori · ammoniti a sistema 3/4</div>
      <div className="flex items-center gap-2 mt-4 text-[#FFCC00]"><Clock size={16} /> <span className="font-black">In attesa delle formazioni ufficiali</span></div>
      <p className="text-xs text-[#A1A1AA] mt-2">Escono stasera appena sono ufficiali gli 11 titolari delle partite delle 20:45-21:00: solo giocatori che partono dal primo minuto.</p>
    </div>
  );
}

function LockedSlipCard({ slip, index }) {
  const { user } = useAuth();
  const accent = ACCENTS[index] || "#39FF14";
  return (
    <div className="surface-raised fade-up relative overflow-hidden flex flex-col" style={{ animationDelay: `${index * 80}ms`, borderTop: `2px solid ${accent}` }} data-testid={`slip-locked-${slipKey(slip)}`}>
      <div className="p-6">
        <div className="label text-[10px]">{slipLabel(slip)}</div>
        <div className="grid grid-cols-3 gap-2 mt-5">
          <div className="border border-[#27272A] p-3"><div className="label text-[9px]">Quota totale</div><div className="heading text-3xl font-black mono" style={{ color: accent }}>{slip.total_odds.toFixed(2)}</div></div>
          <div className="border border-[#27272A] p-3"><div className="label text-[9px]">Prob. vincita</div><div className="heading text-3xl font-black mono">{slip.win_probability}%</div></div>
          <div className="border border-[#27272A] p-3"><div className="label text-[9px]">Eventi</div><div className="heading text-3xl font-black mono">{slip.events}</div></div>
        </div>
      </div>
      <div className="relative flex-1 px-4 pb-6 space-y-2 select-none min-h-[220px]" aria-hidden>
        {["a", "b", "c", "d"].slice(0, Math.min(slip.events, 4)).map((k) => (
          <div key={k} className="border border-[#27272A] p-3 blur-[5px] opacity-60">
            <div className="h-3 w-2/3 bg-[#3f3f46]" /><div className="h-2 w-1/3 bg-[#27272A] mt-2" />
          </div>
        ))}
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center px-4">
            <Lock className="mx-auto" size={22} style={{ color: accent }} />
            <p className="text-xs text-[#A1A1AA] mt-2">Partite e pronostici riservati agli abbonati</p>
            <Link to={user ? "/premium" : "/register?next=/premium"} data-testid={`slip-unlock-${slipKey(slip)}`}
              className="mt-3 inline-block bg-[#39FF14] text-black font-black uppercase tracking-wider text-xs px-4 py-2 hover:bg-white transition-colors">
              Sblocca con Premium
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function SystemInfo({ system }) {
  return (
    <div className="mt-4 border border-[#FFCC00]/50 bg-[#FFCC00]/5 p-3 text-xs" data-testid="slip-system-info">
      <div className="font-black text-[#FFCC00] uppercase tracking-wider text-[10px]">Sistema {system.min_correct}/{system.size} · {system.combos} terzine</div>
      <p className="text-[#A1A1AA] mt-1">Vinci anche con un errore. Puntando 1 € a terzina ({system.combos} € totali): con {system.min_correct} giusti incassi almeno <span className="mono text-white">{system.min_return.toFixed(2)} €</span>, con tutti giusti <span className="mono text-white">{system.max_return.toFixed(2)} €</span>.</p>
    </div>
  );
}

function SinglesInfo({ slip }) {
  return (
    <div className="mt-4 border border-[#27272A] p-3 text-xs" data-testid="slip-singles-info">
      <div className="font-black uppercase tracking-wider text-[10px] text-[#39FF14]">Giocabili anche in singola</div>
      <div className="mt-1 space-y-0.5">
        {slip.selections.map((x) => <div key={x.player_id} className="flex justify-between gap-2"><span className="truncate text-[#A1A1AA]">{x.player}</span><span className="mono">{x.odds.toFixed(2)}</span></div>)}
      </div>
    </div>
  );
}

function SlipCard({ slip, index, date }) {
  const R = RESULT[slip.status] || RESULT.pending;
  const accent = ACCENTS[index] || "#39FF14";
  return (
    <div className="surface-raised fade-up flex flex-col" style={{ animationDelay: `${index * 80}ms`, borderTop: `2px solid ${accent}` }} data-testid={`slip-card-${slipKey(slip)}`}>
      <div className="p-6 border-b border-[#27272A]">
        <div className="flex items-center justify-between">
          <div className="label text-[10px]">{slipLabel(slip)}</div>
          <span className="flex items-center gap-1 text-[10px] font-black uppercase" style={{ color: R.color }} data-testid={`slip-status-${slipKey(slip)}`}><R.icon size={12} /> {R.label}</span>
        </div>
        <div className="heading text-2xl font-black mt-1 leading-tight">{slip.title}</div>
        <div className="grid grid-cols-3 gap-2 mt-5">
          <div className="border border-[#27272A] p-3">
            <div className="label text-[9px]">{slip.system ? "Quota minima" : "Quota totale"}</div>
            <div className="heading text-3xl font-black mono" style={{ color: accent }} data-testid={`slip-total-odds-${slipKey(slip)}`}>{slip.total_odds.toFixed(2)}</div>
          </div>
          <div className="border border-[#27272A] p-3">
            <div className="label text-[9px]">Prob. vincita</div>
            <div className="heading text-3xl font-black mono" data-testid={`slip-win-prob-${slipKey(slip)}`}>{slip.win_probability}%</div>
          </div>
          <div className="border border-[#27272A] p-3">
            <div className="label text-[9px]">Eventi</div>
            <div className="heading text-3xl font-black mono">{slip.selections.length}</div>
          </div>
        </div>
        {slip.strategy && <p className="text-xs text-[#A1A1AA] mt-4 leading-relaxed border-l-2 pl-3" style={{ borderColor: `${accent}66` }}>{slip.strategy}</p>}
        {slip.system && <SystemInfo system={slip.system} />}
        {slip.singles && <SinglesInfo slip={slip} />}
        {slip.status === "won" && <ShareSlipButton slip={slip} date={date} />}
      </div>
      <div className="p-4 space-y-2 flex-1">
        {slip.selections.map((x) => <Selection key={`${x.match_id}-${x.market}-${x.player_id || ""}`} x={x} />)}
      </div>
    </div>
  );
}

function SlipsContent({ date }) {
  const { user } = useAuth();
  const { data, error, mutate } = useSWR(["slips", date], () => api.get("/slips", { params: { date } }).then((r) => r.data), {
    refreshInterval: (d) => (d?.status === "generating" ? 5000 : d?.slips?.some((s) => s.selections?.some((x) => x.live)) ? 60000 : 120000),
  });
  const [busy, setBusy] = useState(false);

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post("/slips/regenerate");
      toast.success("Rigenerazione avviata");
      mutate({ ...data, status: "generating" }, { revalidate: false });
      setTimeout(() => mutate(), 3000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="text-sm text-[#FF3B30]">{formatApiError(error)}</div>;
  if (!data) return <Loader2 className="animate-spin text-[#A1A1AA]" />;
  if (data.status === "generating" && !data.slips.length)
    return (
      <div className="surface-raised p-10 text-center" data-testid="slips-generating">
        <Loader2 size={28} className="animate-spin mx-auto text-[#39FF14]" />
        <div className="heading text-xl font-black mt-4">L&apos;AI sta costruendo le schedine…</div>
        <p className="text-sm text-[#A1A1AA] mt-2">Analisi di quote, forma e statistiche delle partite di oggi e dei prossimi 4 giorni (circa 5 minuti).</p>
      </div>
    );
  if (!data.slips.length)
    return <div data-status={data.status} className="border border-dashed border-[#27272A] p-10 text-center text-sm text-[#A1A1AA]" data-testid="slips-empty">{data.note || "Nessuna schedina disponibile per questo giorno."}</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-[#71717A] mono">
        <span>Generate: {new Date(data.generated_at).toLocaleString("it-IT")} · {data.candidates} partite analizzate</span>
        {user?.role === "admin" && (
          <button onClick={regenerate} disabled={busy || data.status === "generating"} data-testid="slips-regenerate" className="flex items-center gap-1.5 text-[#007AFF] hover:text-[#39FF14] disabled:opacity-50 transition-colors">
            <RefreshCw size={12} className={data.status === "generating" ? "animate-spin" : ""} /> {data.status === "generating" ? "Rigenerazione…" : "Rigenera (admin)"}
          </button>
        )}
      </div>
      {data.includes_next_day && (
        <div className="border border-[#007AFF]/40 bg-[#007AFF]/5 px-4 py-3 text-xs text-[#A1A1AA]" data-testid="slips-next-day-banner">
          Le long time (bomba 60 e 100) e la bomba corner usano anche partite dei prossimi giorni: vedi data e ora di ogni evento.
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="slips-grid">
        {data.slips.map((s, i) => (s.locked ? <LockedSlipCard key={slipKey(s)} slip={s} index={i} /> : <SlipCard key={slipKey(s)} slip={s} index={i} date={data.date} />))}
        {data.scorer_status === "wait" && <ScorerPending />}
      </div>
      <p className="text-[11px] text-[#52525b] leading-relaxed max-w-3xl">
        Le probabilità sono stime statistiche e non garantiscono la vincita. Gioca responsabilmente: il gioco è vietato ai minori e può causare dipendenza.
      </p>
    </div>
  );
}

function NbaSection({ date }) {
  const { data } = useSWR(["nba", date], () => api.get("/nba/slips", { params: date ? { date } : {} }).then((r) => r.data), {
    refreshInterval: (d) => (d?.status === "generating" ? 8000 : 120000),
  });
  if (!data) return null;
  return (
    <section className="space-y-4" data-testid="nba-section">
      <div className="label flex items-center gap-2 text-[#FF7A00]"><Ticket size={12} /> NBA stanotte · Over punti quota 5 e testa a testa quota 4</div>
      {data.status === "generating" && <div className="flex items-center gap-2 text-sm text-[#A1A1AA]" data-testid="nba-generating"><Loader2 size={14} className="animate-spin" /> Analisi delle medie punti in casa e in trasferta...</div>}
      {data.status === "scheduled" && <p className="text-sm text-[#A1A1AA]" data-testid="nba-scheduled">Le schedine NBA escono ogni sera alle {data.publish_hour}:00 con le partite della notte e del giorno dopo.</p>}
      {data.status === "ready" && !data.slips.length && <p className="text-sm text-[#A1A1AA]" data-testid="nba-empty">Stanotte nessuna combinazione supera il 73% per ogni selezione: nessuna schedina NBA.</p>}
      {data.slips.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="nba-slips-grid">
          {data.slips.map((s, i) => <SlipCard key={s.id} slip={s} index={i} date={data.date} />)}
        </div>
      )}
    </section>
  );
}

export default function Slips() {
  const { user } = useAuth();
  const [date, setDate] = useState(romeDate(0));
  const pickDay = (d) => {
    setDate(d);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <div className="min-h-screen px-6 lg:px-10 py-10 max-w-7xl mx-auto">
      <Link to={user?.is_premium ? "/" : "/premium"} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="slips-back-link">
        <ArrowLeft size={14} strokeWidth={1.5} /> {user?.is_premium ? "Tutte le partite" : "GOAL.AI Premium"}
      </Link>
      <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label flex items-center gap-2 text-[#39FF14]"><Ticket size={12} /> {user?.is_premium ? "Premium" : "Riservate agli abbonati"}</div>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2">Schedine del giorno</h1>
          <p className="text-sm text-[#A1A1AA] mt-2 max-w-xl">Ogni giorno, con le partite europee: quota 2, due quota 2,5 sicure, quota 3, 5, 10, 20 e solo cartellini quota 25 con le partite di oggi; long time bomba 60 e 100, super corner 4 partite Over 11,5 e mega listona di soli Over 1,5 (quota 60-70) con partite dei prossimi 4 giorni; in serata 3 marcatori (anche in singola) e ammoniti a sistema 3/4 con le formazioni ufficiali; ogni sera alle 19 NBA Over punti quota 5 e testa a testa quota 4 (ogni selezione almeno 73%). Ogni partita al massimo 2 volte e mai con lo stesso mercato; mai il No Goal e nessuna quota sotto 1,20.</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          {user?.is_premium && <NotifyToggle />}
          <div className="flex gap-2">
            {[[0, "Oggi"], [-1, "Ieri"], [-2, "2 giorni fa"]].map(([o, l]) => (
              <button key={o} onClick={() => setDate(romeDate(o))} data-testid={`slips-day-${o}`}
                className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${date === romeDate(o) ? "bg-[#39FF14] text-black border-[#39FF14]" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {user !== null && !user?.is_premium && <div className="mt-8"><WinShowcase /></div>}
      <div className="mt-10">
        {user === null ? (
          <Loader2 className="animate-spin text-[#A1A1AA]" />
        ) : (
          <div className="space-y-10">
            <SlipsContent date={date} />
            {user?.is_premium && <NbaSection date={date === romeDate(0) ? null : date} />}
            {user?.is_premium && <SlipsHistory onPickDay={pickDay} />}
            <WinsGallery limit={4} />
          </div>
        )}
      </div>
    </div>
  );
}
